import type { Card } from 'ts-fsrs';
import type { CardKey, Skill } from '../../types';

/** One persisted FSRS card plus denormalized fields for indexing. */
export interface CardRecord {
  key: CardKey;
  code: string;
  skill: Skill;
  /** The ts-fsrs card. Holds `Date` values — see cardToJson/cardFromJson. */
  card: Card;
  /** Copy of card.due, used as the IndexedDB `by-due` index key. */
  due: Date;
}

/** Append-only review log entry. `id` is assigned by the backend on write. */
export interface ReviewRecord {
  id: string;
  key: CardKey;
  /** ts-fsrs Rating numeric value. */
  rating: number;
  /** Whether the answer was counted correct (rating > Again). */
  correct: boolean;
  /**
   * The species the learner actually named, when the question was a pick from
   * a list of choices. Equal to the card's own code on a right answer.
   *
   * Absent means there was nothing to record — a self-graded flip card, or a
   * review written by a build before this field existed — never "they got it
   * right". Keeping it on correct answers too is what makes the confusion
   * numbers exact: "you call this one a Winter Wren four times in five" needs
   * the five, and the five is the number of picks, not the number of reviews.
   */
  picked?: string;
  /** Epoch milliseconds. */
  ts: number;
}

/** A review as the app writes it; the backend supplies the id. */
export type ReviewInput = Omit<ReviewRecord, 'id'>;

/** Everything the app holds in memory for one session. */
export interface Snapshot {
  cards: CardRecord[];
  /**
   * Reviews newer than the hydration window only (see HYDRATE_REVIEW_DAYS).
   * The full log is reachable via `allReviews()` for export.
   */
  reviews: ReviewRecord[];
  settings: Record<string, unknown>;
}

/**
 * Persistence for a single user's progress. Two implementations: IndexedDB
 * (local development, and the offline fallback of last resort) and PocketBase
 * (the deployed source of truth). The app reads from an in-memory snapshot
 * hydrated once at boot, so only writes reach the backend — see core/db.ts.
 */
export interface StorageBackend {
  /** Human label for the settings screen, e.g. "PocketBase (you@example.com)". */
  readonly label: string;

  /**
   * Load the session snapshot. `sinceTs` bounds the review log; cards and
   * settings are always complete.
   */
  hydrate(sinceTs: number): Promise<Snapshot>;

  /** Insert or update the card for `rec.key`. */
  putCard(rec: CardRecord): Promise<void>;

  /**
   * Remove the card for `key`, if there is one.
   *
   * Only undo uses this, and only for a card that did not exist a moment ago:
   * taking back the first answer a species ever got has to leave no card at
   * all, because a card is what "you have met this bird" means everywhere else
   * — in the mastery denominator, in the Library's dots, and in the study queue,
   * where an existing card moves a species out of the never-met tier for good.
   * Writing a blank card back would be a quieter lie than leaving the graded one.
   */
  deleteCard(key: CardKey): Promise<void>;

  /** Append one review. */
  logReview(rec: ReviewInput): Promise<ReviewRecord>;

  /**
   * Remove one review by id. The log is append-only in every other respect;
   * this exists so an answer taken back leaves nothing behind in the accuracy
   * numbers or the confusion pairs it would otherwise have counted toward.
   */
  deleteReview(id: string): Promise<void>;

  /** Persist the whole settings map (xp, streak, day markers). */
  saveSettings(settings: Record<string, unknown>): Promise<void>;

  /** The complete review log, for JSON export. */
  allReviews(): Promise<ReviewRecord[]>;

  /** Wipe this user's progress and rewrite it from `snap` (JSON import). */
  replaceAll(snap: Snapshot): Promise<void>;
}

// ---- Card JSON serialization ----------------------------------------------
//
// IndexedDB's structured clone round-trips `Date` unchanged, but JSON does not:
// over the PocketBase REST API a Date becomes an ISO string, and ts-fsrs would
// then blow up on `card.due.getTime()`. Both directions go through these two
// functions so a card is revived identically no matter where it was stored.

export function cardToJson(card: Card): Record<string, unknown> {
  return {
    ...card,
    due: card.due.toISOString(),
    last_review: card.last_review ? card.last_review.toISOString() : null,
  };
}

export function cardFromJson(raw: unknown): Card {
  const r = (raw ?? {}) as Record<string, unknown>;
  const card: Card = {
    due: new Date(String(r['due'])),
    stability: Number(r['stability'] ?? 0),
    difficulty: Number(r['difficulty'] ?? 0),
    elapsed_days: Number(r['elapsed_days'] ?? 0),
    scheduled_days: Number(r['scheduled_days'] ?? 0),
    learning_steps: Number(r['learning_steps'] ?? 0),
    reps: Number(r['reps'] ?? 0),
    lapses: Number(r['lapses'] ?? 0),
    state: Number(r['state'] ?? 0),
  };
  const last = r['last_review'];
  if (typeof last === 'string' && last !== '') card.last_review = new Date(last);
  return card;
}
